// FormularioDialog — formulario modal genérico para las operaciones financieras
// (registrar un aporte, desembolsar un crédito, pagar una cuota...).
//
// campos: [{ campo, label, tipo: 'texto'|'numero'|'fecha'|'select'|'switch'|'area',
//            opciones?: [{ valor, etiqueta }], requerido?, positivo?, ayuda? }]
// onGuardar(valores) debe lanzar Error si el backend rechaza la operación: el mensaje
// (p. ej. «el período está cerrado») se muestra dentro del diálogo, que sigue abierto.

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const vacio = (v) => v === '' || v === null || v === undefined;

export default function FormularioDialog({ abierto, titulo, descripcion, campos, inicial = {}, textoBoton = 'Guardar', onGuardar, onClose }) {
  const [valores, setValores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (abierto) {
      setValores(inicial || {});
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const set = (campo, valor) => setValores((v) => ({ ...v, [campo]: valor }));

  const invalido = campos.some((c) => {
    const v = valores[c.campo];
    if (c.requerido && vacio(v)) return true;
    if (c.positivo && !(Number(v) > 0)) return true;
    return false;
  });

  const guardar = async () => {
    setEnviando(true);
    setError('');
    try {
      const salida = {};
      campos.forEach((c) => {
        const v = valores[c.campo];
        salida[c.campo] = c.tipo === 'numero' ? (vacio(v) ? 0 : Number(v)) : c.tipo === 'switch' ? Boolean(v) : v ?? '';
      });
      await onGuardar(salida);
      onClose();
    } catch (e) {
      setError(e?.message || 'No se pudo completar la operación.');
    } finally {
      setEnviando(false);
    }
  };

  const control = (c) => {
    const v = valores[c.campo];
    if (c.tipo === 'select') {
      return (
        <FormControl key={c.campo} fullWidth required={c.requerido}>
          <InputLabel>{c.label}</InputLabel>
          <Select label={c.label} value={v ?? ''} onChange={(e) => set(c.campo, e.target.value)}>
            {(c.opciones || []).map((o) => (
              <MenuItem key={o.valor} value={o.valor}>
                {o.etiqueta}
              </MenuItem>
            ))}
          </Select>
          {c.ayuda && <FormHelperText>{c.ayuda}</FormHelperText>}
        </FormControl>
      );
    }
    if (c.tipo === 'switch') {
      return (
        <FormControlLabel
          key={c.campo}
          control={<Switch checked={Boolean(v)} onChange={(e) => set(c.campo, e.target.checked)} />}
          label={c.label}
        />
      );
    }
    return (
      <TextField
        key={c.campo}
        label={c.label}
        required={c.requerido}
        type={c.tipo === 'numero' ? 'number' : c.tipo === 'fecha' ? 'date' : 'text'}
        multiline={c.tipo === 'area'}
        minRows={c.tipo === 'area' ? 2 : undefined}
        value={v ?? ''}
        onChange={(e) => set(c.campo, e.target.value)}
        InputLabelProps={c.tipo === 'fecha' ? { shrink: true } : undefined}
        helperText={c.ayuda}
        fullWidth
      />
    );
  };

  return (
    <Dialog open={Boolean(abierto)} onClose={enviando ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{titulo}</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {descripcion && (
          <Typography variant="body2" color="text.secondary">
            {descripcion}
          </Typography>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {campos.map(control)}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={enviando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={guardar} disabled={invalido || enviando} startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : null}>
          {textoBoton}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
